// A topic's controls (CHAT-UX): every lifecycle action visible and one click away. The quick
// actions (pin or unpin, close or reopen, archive or restore) sit as small buttons in the
// topic's header and over a sidebar row on hover; the "…" holds the rest (follow, rename, move
// to another channel, copy the link, delete). Any member pins, closes and reopens; unpin,
// archive, restore and delete are the channel's admins' (the API answers 403 to anyone else),
// so those show for them only. A new topic comes from the "+" beside a channel in the sidebar
// and the "New topic" button in the channel's header.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  Input,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BellIcon,
  CircleCheckIcon,
  CornerUpRightIcon,
  EllipsisIcon,
  LinkIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  RotateCcwIcon,
  Trash2Icon,
} from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { useOptimisticMutation } from "../../api/optimistic";
import { keepFocus } from "../../focus/focus";
import { useCanManage } from "./channel-settings";
import {
  channelLabel,
  isAgentRoom,
  isDm,
  MAIN_TOPIC,
  qualifiedChannelName,
  spaceQualifiers,
} from "./chat-model";
import {
  type Channel,
  channelsQuery,
  chatKeys,
  followsQuery,
  spacesQuery,
  type TopicSummary,
} from "./chat-queries";
import type { ChatContext } from "./use-chat";

type TopicRef = Pick<
  TopicSummary,
  "id" | "name" | "channel_id" | "status" | "pinned_at" | "archived_at"
>;

const fail = (e: unknown) =>
  toast.add({ title: e instanceof Error ? e.message : "That did not go through", type: "error" });

/** The lifecycle mutations of one topic, each refreshing the channel's lists on success. */
function useTopicMutations(chat: ChatContext, topic: TopicRef) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, topic.channel_id) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.topic(co, topic.id) }),
    ]);
  const params = { co, id: topic.id };
  // CHAT-PERF: pin and close show at once in the sidebar, the chips and the header (the
  // channel's topic list and the topic's detail), and roll back if refused.
  const patchTopic = (fields: Partial<TopicSummary>) => {
    queryClient.setQueryData<{ data: TopicSummary[] }>(
      chatKeys.topics(co, topic.channel_id),
      (d) => d && { ...d, data: d.data.map((t) => (t.id === topic.id ? { ...t, ...fields } : t)) },
    );
    queryClient.setQueryData<Record<string, unknown>>(
      chatKeys.topic(co, topic.id),
      (d) => d && { ...d, ...fields },
    );
  };
  const keys = () => [chatKeys.topics(co, topic.channel_id), chatKeys.topic(co, topic.id)];
  const resolve = useOptimisticMutation<boolean>({
    keys,
    apply: (resolved) => patchTopic({ status: resolved ? "resolved" : "open" }),
    mutationFn: (resolved) => api.client.topics.resolve({ params, body: { resolved } }),
    settle: keys,
    onFailure: (e, _v, rollback) => {
      rollback();
      fail(e);
    },
  });
  const pin = useOptimisticMutation<boolean>({
    keys,
    apply: (pinned) =>
      patchTopic({ pinned_at: pinned ? (topic.pinned_at ?? new Date().toISOString()) : null }),
    mutationFn: (pinned) => api.client.topics.pin({ params, body: { pinned } }),
    onSuccess: (_r, pinned) =>
      toast.add({ title: pinned ? "Pinned to the top" : "Unpinned", type: "success" }),
    settle: keys,
    onFailure: (e, _v, rollback) => {
      rollback();
      fail(e);
    },
  });
  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      archived ? api.client.topics.archive({ params }) : api.client.topics.restore({ params }),
    onSuccess: (_r, archived) => {
      void refresh();
      toast.add({
        title: archived ? `Archived "${topic.name}"` : `"${topic.name}" is back`,
        type: "success",
      });
    },
    onError: fail,
  });
  return { resolve, pin, archive, refresh };
}

/**
 * The visible quick actions: Pin / Unpin (unpin for admins), Close / Reopen, Archive / Restore
 * (admins). `compact` renders icon-only buttons (the sidebar's hover row); otherwise each has
 * its word.
 */
export function TopicQuickActions({
  chat,
  channel,
  topic,
  compact = false,
  className,
}: {
  chat: ChatContext;
  channel: Pick<Channel, "id">;
  topic: TopicRef;
  compact?: boolean;
  className?: string | undefined;
}) {
  const manages = useCanManage(chat, channel);
  const { resolve, pin, archive } = useTopicMutations(chat, topic);
  if (chat.meId === null || chat.readOnly) return null;
  const closed = topic.status === "resolved";
  const pinned = topic.pinned_at !== null;
  const archived = topic.archived_at !== null;
  const size = compact ? ("icon-xs" as const) : ("xs" as const);
  const btn = (label: string, icon: ReactNode, onClick: () => void, pending: boolean) => (
    <Button
      variant="ghost"
      size={size}
      aria-label={label}
      title={label}
      disabled={pending}
      onClick={onClick}
      className={compact ? "text-faint hover:text-foreground" : "text-secondary"}
    >
      {icon}
      {compact ? null : <span className="max-sm:sr-only">{label}</span>}
    </Button>
  );
  return (
    <div
      data-slot="topic-actions"
      className={["flex items-center gap-px", className ?? ""].filter(Boolean).join(" ")}
    >
      {archived ? (
        manages ? (
          btn(
            "Restore",
            <ArchiveRestoreIcon data-icon="inline-start" />,
            () => archive.mutate(false),
            archive.isPending,
          )
        ) : null
      ) : (
        <>
          {pinned
            ? manages
              ? btn(
                  "Unpin",
                  <PinOffIcon data-icon="inline-start" />,
                  () => pin.mutate(false),
                  pin.isPending,
                )
              : null
            : btn(
                "Pin",
                <PinIcon data-icon="inline-start" />,
                () => pin.mutate(true),
                pin.isPending,
              )}
          {closed
            ? btn(
                "Reopen",
                <RotateCcwIcon data-icon="inline-start" />,
                () => resolve.mutate(false),
                resolve.isPending,
              )
            : btn(
                "Close",
                <CircleCheckIcon data-icon="inline-start" />,
                () => resolve.mutate(true),
                resolve.isPending,
              )}
          {manages
            ? btn(
                "Archive",
                <ArchiveIcon data-icon="inline-start" />,
                () => archive.mutate(true),
                archive.isPending,
              )
            : null}
        </>
      )}
    </div>
  );
}

/**
 * The topic's "…": Follow, Rename, Move to channel ▸, Copy link, and (admins) Delete behind a
 * confirmation. After a delete `onDeleted` runs (the screen leaves the topic for its channel).
 */
export function TopicMenu({
  chat,
  channel,
  topic,
  className,
  onDeleted,
}: {
  chat: ChatContext;
  channel: Pick<Channel, "id">;
  topic: TopicRef;
  className?: string | undefined;
  onDeleted?: (() => void) | undefined;
}) {
  const { api, co, company } = chat;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const manages = useCanManage(chat, channel);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const follows = useQuery({ ...followsQuery(api, co), enabled: chat.meId !== null, retry: false });
  const channels = useQuery({ ...channelsQuery(api, co), enabled: chat.meId !== null });
  const spaces = useQuery({ ...spacesQuery(api, co), enabled: chat.meId !== null });
  const following =
    follows.data?.data.some((f) => f.subject_kind === "topic" && f.subject_id === topic.id) ??
    false;
  const { refresh } = useTopicMutations(chat, topic);
  const follow = useMutation({
    mutationFn: async (on: boolean) => {
      if (on)
        await api.client.follows.put({
          params: { co },
          body: { subject_kind: "topic", subject_id: topic.id, level: "all" },
        });
      else
        await api.client.follows.delete({
          params: { co },
          query: { subject_kind: "topic", subject_id: topic.id },
        });
    },
    onSuccess: (_r, on) => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.follows(co) });
      toast.add({ title: on ? "Following this topic" : "Unfollowed", type: "success" });
    },
    onError: fail,
  });
  const move = useMutation({
    mutationFn: (channelId: string) =>
      api.client.topics.move({ params: { co, id: topic.id }, body: { channel_id: channelId } }),
    onSuccess: async (t) => {
      await Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, t.channel_id) }),
      ]);
      toast.add({ title: `Moved "${topic.name}"`, type: "success" });
      void navigate({
        to: "/c/$slug/chat/$channelId/$topicId",
        params: { slug: company.slug, channelId: t.channel_id, topicId: t.id },
        search: keepFocus as never,
      });
    },
    onError: (e) =>
      toast.add({
        title:
          e instanceof Error && /409|conflict/i.test(e.message)
            ? "That channel has a topic with this name"
            : "Could not move",
        type: "error",
      }),
  });
  const remove = useMutation({
    mutationFn: () => api.client.topics.delete({ params: { co, id: topic.id } }),
    onSuccess: async () => {
      setDeleting(false);
      await queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, topic.channel_id) });
      toast.add({ title: `Deleted "${topic.name}"`, type: "success" });
      onDeleted?.();
    },
    onError: fail,
  });
  if (chat.meId === null || chat.readOnly) return null;
  const copyLink = async () => {
    const url = `${window.location.origin}/c/${company.slug}/chat/${topic.channel_id}/${topic.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.add({ title: "Link copied", type: "success" });
    } catch {
      toast.add({ title: url, type: "info" });
    }
  };
  const targets = (channels.data?.data ?? []).filter(
    (c) => c.id !== topic.channel_id && !isDm(c) && !isAgentRoom(c),
  );
  // Same-named channels in two spaces carry the space ("#operations · Northwind Ops").
  const qualifiers = spaceQualifiers(targets, spaces.data?.data ?? []);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Topic menu: ${topic.name}`}
              title="More"
              {...(className ? { className } : {})}
            />
          }
        >
          <EllipsisIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          <DropdownMenuCheckboxItem checked={following} onCheckedChange={(on) => follow.mutate(on)}>
            <BellIcon />
            Follow topic
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setRenaming(true)}>
            <PencilIcon />
            Rename
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <CornerUpRightIcon />
              Move to channel
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-80 min-w-44 overflow-y-auto">
              {targets.length ? (
                targets.map((c) => (
                  <DropdownMenuItem key={c.id} onClick={() => move.mutate(c.id)}>
                    {qualifiedChannelName(c, qualifiers)}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>No other channel</DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={() => void copyLink()}>
            <LinkIcon />
            Copy link
          </DropdownMenuItem>
          {manages ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => setDeleting(true)}>
                <Trash2Icon />
                Delete topic
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {renaming ? (
        <RenameTopicDialog chat={chat} topic={topic} onClose={() => setRenaming(false)} />
      ) : null}
      <AlertDialog
        open={deleting}
        onOpenChange={(o) => !o && !remove.isPending && setDeleting(false)}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{topic.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Its messages are removed for everyone and the topic leaves every list. Archive keeps
              them instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Renames a topic; a name another topic of the channel has is refused (409). */
function RenameTopicDialog({
  chat,
  topic,
  onClose,
}: {
  chat: ChatContext;
  topic: TopicRef;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const [name, setName] = useState(topic.name);
  const rename = useMutation({
    mutationFn: (next: string) =>
      api.client.topics.rename({ params: { co, id: topic.id }, body: { name: next } }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, topic.channel_id) }),
        queryClient.invalidateQueries({ queryKey: chatKeys.topic(co, topic.id) }),
      ]);
      onClose();
    },
    onError: (e) =>
      toast.add({
        title:
          e instanceof Error && /409|conflict/i.test(e.message)
            ? "A topic has that name"
            : "Could not rename",
        type: "error",
      }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = name.trim();
    if (next && next !== topic.name) rename.mutate(next);
    else onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !rename.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Rename topic</DialogTitle>
            <DialogDescription>Its messages and links stay where they are.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="topic-name">Name</FieldLabel>
            <Input
              id="topic-name"
              value={name}
              maxLength={60}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!name.trim() || rename.isPending}>
              Rename
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A new topic in a channel (the sidebar's "+" beside the channel, the header's "New topic"):
 * one name; an existing name opens that topic. Opens it when made.
 */
export function NewTopicDialog({
  chat,
  channel,
  onClose,
}: {
  chat: ChatContext;
  channel: Pick<Channel, "id" | "kind" | "name">;
  onClose: () => void;
}) {
  const { api, co, company } = chat;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: (next: string) =>
      api.client.channels.createTopic({ params: { co, id: channel.id }, body: { name: next } }),
    onSuccess: async (t) => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.topics(co, channel.id) });
      onClose();
      void navigate({
        to: "/c/$slug/chat/$channelId/$topicId",
        params: { slug: company.slug, channelId: channel.id, topicId: t.id },
        search: keepFocus as never,
      });
    },
    onError: fail,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = name.trim();
    // CHAT-TOPICS-SEPARATE: that name is the channel's own conversation, not a topic.
    if (next.toLowerCase() === MAIN_TOPIC) {
      toast.add({ title: "Pick another name", type: "error" });
      return;
    }
    if (next) create.mutate(next);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !create.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>New topic in {channelLabel(channel, chat)}</DialogTitle>
            <DialogDescription>
              A topic keeps one conversation apart in the channel. Pin it to keep it on top.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="new-topic-name">Name</FieldLabel>
            <Input
              id="new-topic-name"
              value={name}
              maxLength={60}
              autoFocus
              placeholder="W44 shortlist"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              Create topic
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
